import { useState, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';

// Preset types for quick selection
interface Preset {
  label: string;
  getValue: () => { start: Date; end: Date };
}

interface TimeRangeSelectorProps {
  startDate: Date;
  endDate: Date;
  onChange: (start: Date, end: Date) => void;
  className?: string;
}

// Helper to create date at start of day in local timezone
const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper to create date at end of day in local timezone
const endOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

// Format a Date as a local YYYY-MM-DD string (for <input type="date">).
// Avoids toISOString() which would shift the displayed date by a day in non-UTC zones.
const toLocalDateString = (d: Date): string => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Parse a YYYY-MM-DD string as local midnight (matches what the picker shows).
const fromLocalDateString = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

// Create preset options
const getPresets = (): Preset[] => {
  const today = startOfDay(new Date());
  const yesterday = startOfDay(new Date(today.getTime() - 86400000));

  return [
    {
      label: 'Today',
      getValue: () => ({ start: today, end: endOfDay(new Date()) }),
    },
    {
      label: 'Yesterday',
      getValue: () => ({ start: yesterday, end: endOfDay(yesterday) }),
    },
    {
      label: 'Last 7 Days',
      getValue: () => ({
        start: new Date(today.getTime() - 6 * 86400000),
        end: endOfDay(new Date()),
      }),
    },
    {
      label: 'Last 14 Days',
      getValue: () => ({
        start: new Date(today.getTime() - 13 * 86400000),
        end: endOfDay(new Date()),
      }),
    },
    {
      label: 'Last 30 Days',
      getValue: () => ({
        start: new Date(today.getTime() - 29 * 86400000),
        end: endOfDay(new Date()),
      }),
    },
    {
      label: 'This Month',
      getValue: () => {
        const now = new Date();
        return {
          start: new Date(now.getFullYear(), now.getMonth(), 1),
          end: endOfDay(new Date()),
        };
      },
    },
    {
      label: 'Last Month',
      getValue: () => {
        const now = new Date();
        const firstOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          start: firstOfLastMonth,
          end: endOfDay(lastOfLastMonth),
        };
      },
    },
    {
      label: 'This Quarter',
      getValue: () => {
        const now = new Date();
        const quarter = Math.floor(now.getMonth() / 3);
        return {
          start: new Date(now.getFullYear(), quarter * 3, 1),
          end: endOfDay(new Date()),
        };
      },
    },
    {
      label: 'Year to Date',
      getValue: () => {
        const now = new Date();
        return {
          start: new Date(now.getFullYear(), 0, 1),
          end: endOfDay(new Date()),
        };
      },
    },
    {
      label: 'All Time',
      getValue: () => ({
        start: new Date('2020-01-01'),
        end: endOfDay(new Date()),
      }),
    },
  ];
};

export function TimeRangeSelector({
  startDate,
  endDate,
  onChange,
  className = '',
}: TimeRangeSelectorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCustom, setShowCustom] = useState(false);
  const [customStart, setCustomStart] = useState(toLocalDateString(startDate));
  const [customEnd, setCustomEnd] = useState(toLocalDateString(endDate));
  // Initialize to "Last 14 Days" since that's the default in DashboardPage
  const [selectedPreset, setSelectedPreset] = useState<string>('Last 14 Days');

  const presets = getPresets();

  // Format date for display
  const formatDateDisplay = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Update custom date inputs when props change (for display sync)
  useEffect(() => {
    setCustomStart(toLocalDateString(startDate));
    setCustomEnd(toLocalDateString(endDate));
  }, [startDate, endDate]);

  const handlePresetClick = (preset: Preset) => {
    const { start, end } = preset.getValue();
    onChange(start, end);
    setSelectedPreset(preset.label);
    setShowDropdown(false);
    setShowCustom(false);
  };

  const handleCustomApply = () => {
    // fromLocalDateString already returns local midnight; endOfDay extends to 23:59:59.999.
    const start = fromLocalDateString(customStart);
    const end = endOfDay(fromLocalDateString(customEnd));
    onChange(start, end);
    setSelectedPreset('Custom');
    setShowCustom(false);
    setShowDropdown(false);
  };

  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
      >
        <Calendar className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-700">
          {selectedPreset === 'Custom'
            ? `${formatDateDisplay(startDate)} - ${formatDateDisplay(endDate)}`
            : selectedPreset || 'Select Range'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {showDropdown && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
          <div className="p-2">
            <div className="text-xs font-semibold text-gray-500 uppercase px-2 py-1">
              Quick Select
            </div>
            {presets.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handlePresetClick(preset)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                  selectedPreset === preset.label
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                {preset.label}
              </button>
            ))}
            <div className="border-t border-gray-200 mt-2 pt-2">
              <button
                onClick={() => setShowCustom(!showCustom)}
                className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors ${
                  showCustom ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 text-gray-700'
                }`}
              >
                Custom Range...
              </button>
            </div>
          </div>

          {showCustom && (
            <div className="border-t border-gray-200 p-3">
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <button
                  onClick={handleCustomApply}
                  className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Click outside to close */}
      {showDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowDropdown(false);
            setShowCustom(false);
          }}
        />
      )}
    </div>
  );
}
